import posthog from "posthog-js";

/** Whether posthog.init() has been called. posthog-js queues events internally, so it is safe to call capture/identify immediately after init returns. */
let isInitialized = false;
let clickListenerAttached = false;

/**
 * Initialize PostHog analytics with autocapture, session recording, and heatmaps.
 *
 * No-op if the API key is missing. Safe to call multiple times — subsequent
 * calls are ignored once initialized.
 */
export function initAnalytics(): void {
  if (isInitialized) return;

  const apiKey = process.env.REACT_APP_POSTHOG_KEY;
  const apiHost = process.env.REACT_APP_POSTHOG_HOST;

  if (!apiKey) {
    return;
  }

  posthog.init(apiKey, {
    api_host: apiHost || "https://us.i.posthog.com",
    autocapture: true,
    capture_pageview: true,
    capture_pageleave: true,
    enable_recording_console_log: false,
    enable_heatmaps: true,
    session_recording: {
      maskAllInputs: true,
      recordCrossOriginIframes: false,
    },
  });

  isInitialized = true;

  // Global click listener — fires custom event using data-attr value as event name (e.g., "sidebar-nav-calendar").
  // Uses event delegation so one listener covers all labeled elements.
  // Event names are kebab-case (from data-attr values), intentionally distinct
  // from snake_case business events (e.g., calendar_viewed) for easy filtering.
  if (!clickListenerAttached) {
    clickListenerAttached = true;
    document.addEventListener(
      "click",
      (e) => {
        let rawTarget: Element | null = null;
        if (e.target instanceof Element) {
          rawTarget = e.target;
        } else if (e.target instanceof Node) {
          rawTarget = e.target.parentElement;
        }
        if (!rawTarget) return;
        const target = rawTarget.closest("[data-attr]");
        if (!target) return;
        const attr = target.getAttribute("data-attr");
        if (!attr || !/^[a-z][a-z0-9-]{1,60}$/.test(attr)) return;
        trackEvent(attr, { page: window.location.pathname });
      },
      { capture: true },
    );
  }
}

/**
 * Identify the current user in PostHog.
 *
 * @param userId - Unique user identifier
 * @param properties - User properties to attach (hotelName, email, role)
 */
export function identifyUser(
  userId: string,
  properties: Record<string, string>,
): void {
  if (!isInitialized) return;
  posthog.identify(userId, properties);
}

/**
 * Track a named event with optional properties.
 *
 * No-op if PostHog is not initialized. Safe to call at any time.
 */
export function trackEvent(
  eventName: string,
  properties?: Record<string, unknown>,
): void {
  if (!isInitialized) return;
  posthog.capture(eventName, properties);
}

/**
 * Reset the PostHog identity (call on logout / session expiry).
 *
 * This clears the distinct_id so the next user on the same browser
 * is not incorrectly associated with the previous user.
 */
export function resetAnalytics(): void {
  if (!isInitialized) return;
  posthog.reset();
}
